import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '../auth/AuthContext'

/**
 * "View as kid" is a presentation filter only - see the plan's own note, echoed in the
 * server's openapi.yaml description: any request carrying the parent session cookie can
 * still reach every endpoint regardless of what's selected here. This just controls what
 * the UI chooses to show.
 */
interface ViewAsKidContextValue {
  viewingChildId: number | null
  viewAsKid: (childId: number) => void
  viewAsParent: () => void
}

const ViewAsKidContext = createContext<ViewAsKidContextValue | null>(null)

const STORAGE_KEY = 'parentbank.viewingChildId'

export function ViewAsKidProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  const [viewingChildId, setViewingChildId] = useState<number | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      return stored ? Number(stored) : null
    } catch {
      return null
    }
  })

  // A stale "viewing as kid" selection must not survive a logout or a forced 401 bounce -
  // otherwise logging back in in the same tab drops straight into the restricted kid view
  // instead of the parent dashboard, even though the new session has full access.
  useEffect(() => {
    if (status === 'unauthenticated') persist(null)
  }, [status])

  function persist(value: number | null) {
    setViewingChildId(value)
    try {
      if (value === null) sessionStorage.removeItem(STORAGE_KEY)
      else sessionStorage.setItem(STORAGE_KEY, String(value))
    } catch {
      // A private-browsing tab can throw on storage access - the in-memory state still works.
    }
  }

  return (
    <ViewAsKidContext.Provider
      value={{
        viewingChildId,
        viewAsKid: (childId) => persist(childId),
        viewAsParent: () => persist(null),
      }}
    >
      {children}
    </ViewAsKidContext.Provider>
  )
}

export function useViewAsKid(): ViewAsKidContextValue {
  const ctx = useContext(ViewAsKidContext)
  if (!ctx) throw new Error('useViewAsKid must be used within a ViewAsKidProvider')
  return ctx
}
